import React, { useState, useEffect, useCallback } from 'react';

const INITIAL = { firstName: '', lastName: '', email: '', org: '', role: '', message: '' };

const DemoForm = ({ isOpen, onClose }) => {
  const open = isOpen;
  const [fields, setFields] = useState(INITIAL);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleChange = (e) => {
    setFields(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    const html = `
      <div style="font-family: sans-serif; max-width: 600px; color: #111;">
        <h2 style="margin-bottom: 24px;">New Demo Request</h2>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 10px 0; border-bottom: 1px solid #eee; color: #666; width: 140px;">Name</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #eee;">${fields.firstName} ${fields.lastName}</td></tr>
          <tr><td style="padding: 10px 0; border-bottom: 1px solid #eee; color: #666;">Work Email</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #eee;">${fields.email}</td></tr>
          <tr><td style="padding: 10px 0; border-bottom: 1px solid #eee; color: #666;">Organization</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #eee;">${fields.org}</td></tr>
          <tr><td style="padding: 10px 0; border-bottom: 1px solid #eee; color: #666;">Role / Title</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #eee;">${fields.role || '—'}</td></tr>
          <tr><td style="padding: 10px 0; color: #666; vertical-align: top;">Message</td>
              <td style="padding: 10px 0;">${fields.message ? fields.message.replace(/\n/g, '<br>') : '—'}</td></tr>
        </table>
      </div>
    `;

    try {
      const res = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_RESEND_API_KEY}`,
        },
        body: JSON.stringify({
          from: 'Arch Demo Requests <onboarding@resend.dev>',
          to: 'dale@validor.co',
          subject: `New Demo Request — ${fields.org}`,
          html,
        }),
      });

      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handleReset = useCallback(() => {
    setFields(INITIAL);
    setStatus('idle');
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <>
      <style>{`
        .demo-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: overlay-in 0.18s ease both;
        }
        @keyframes overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .demo-panel {
          background: #fff;
          border-radius: 16px;
          width: 100%;
          max-width: 540px;
          padding: 48px;
          position: relative;
          animation: panel-in 0.22s ease both;
          max-height: calc(100vh - 48px);
          overflow-y: auto;
        }
        @keyframes panel-in {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .demo-close {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 32px;
          height: 32px;
          border: none;
          background: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          color: #999;
          transition: background 0.15s, color 0.15s;
        }
        .demo-close:hover { background: #f4f4f5; color: #333; }
        .demo-heading {
          font-family: 'Cormorant', serif;
          font-size: 36px;
          font-weight: 600;
          letter-spacing: -0.5px;
          line-height: 1.05;
          color: #0a0a0a;
          margin-bottom: 8px;
        }
        .demo-subhead {
          font-family: 'Figtree', sans-serif;
          font-size: 14px;
          color: #888;
          margin-bottom: 36px;
          line-height: 1.5;
        }
        .demo-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .demo-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
        }
        .demo-field label {
          font-family: 'Figtree', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: #555;
          letter-spacing: 0.01em;
        }
        .demo-field input,
        .demo-field textarea {
          font-family: 'Figtree', sans-serif;
          font-size: 14px;
          color: #0a0a0a;
          background: #fff;
          border: 1px solid #d4d4d8;
          border-radius: 8px;
          padding: 10px 14px;
          outline: none;
          transition: border-color 0.15s;
          width: 100%;
          resize: none;
        }
        .demo-field input::placeholder,
        .demo-field textarea::placeholder {
          color: #bbb;
        }
        .demo-field input:focus,
        .demo-field textarea:focus {
          border-color: #0a0a0a;
        }
        .demo-field textarea { min-height: 88px; }
        .demo-submit {
          width: 100%;
          background: #0a0a0a;
          color: #fff;
          border: none;
          padding: 13px 24px;
          border-radius: 10px;
          font-family: 'Figtree', sans-serif;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          margin-top: 8px;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .demo-submit:hover:not(:disabled) { background: #1a1a1a; }
        .demo-submit:disabled { opacity: 0.6; cursor: default; }
        .demo-success {
          text-align: center;
          padding: 24px 0 8px;
        }
        .demo-success-icon {
          width: 48px;
          height: 48px;
          background: #e6f4ea;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }
        .demo-success-title {
          font-family: 'Cormorant', serif;
          font-size: 28px;
          font-weight: 600;
          color: #0a0a0a;
          margin-bottom: 10px;
          letter-spacing: -0.3px;
        }
        .demo-success-body {
          font-family: 'Figtree', sans-serif;
          font-size: 15px;
          color: #666;
          line-height: 1.6;
          margin-bottom: 32px;
        }
        .demo-error-note {
          font-family: 'Figtree', sans-serif;
          font-size: 13px;
          color: #b91c1c;
          background: #fee2e2;
          border-radius: 8px;
          padding: 10px 14px;
          margin-top: 12px;
          line-height: 1.5;
        }
        @media (max-width: 540px) {
          .demo-panel { padding: 32px 24px; }
          .demo-row { grid-template-columns: 1fr; gap: 0; }
        }
      `}</style>

      <div className="demo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleReset(); }}>
        <div className="demo-panel" role="dialog" aria-modal="true" aria-labelledby="demo-title">
          <button className="demo-close" onClick={handleReset} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          {status === 'success' ? (
            <div className="demo-success">
              <div className="demo-success-icon">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M4 11l5 5 9-9" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="demo-success-title">Request received.</div>
              <p className="demo-success-body">Thanks — we'll be in touch within 24 hours.</p>
              <button className="demo-submit" style={{ maxWidth: 200, margin: '0 auto' }} onClick={handleReset}>
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="demo-heading" id="demo-title">Request a Demo</h2>
              <p className="demo-subhead">We'll walk you through the platform. No commitment required.</p>

              <div className="demo-row">
                <div className="demo-field">
                  <label htmlFor="demo-first">First Name</label>
                  <input
                    id="demo-first"
                    name="firstName"
                    type="text"
                    placeholder="Jane"
                    value={fields.firstName}
                    onChange={handleChange}
                    autoComplete="given-name"
                  />
                </div>
                <div className="demo-field">
                  <label htmlFor="demo-last">Last Name</label>
                  <input
                    id="demo-last"
                    name="lastName"
                    type="text"
                    placeholder="Smith"
                    value={fields.lastName}
                    onChange={handleChange}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="demo-field">
                <label htmlFor="demo-email">Work Email <span style={{ color: '#0a0a0a' }}>*</span></label>
                <input
                  id="demo-email"
                  name="email"
                  type="email"
                  placeholder="jane@university.edu"
                  value={fields.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="demo-field">
                <label htmlFor="demo-org">Organization Name <span style={{ color: '#0a0a0a' }}>*</span></label>
                <input
                  id="demo-org"
                  name="org"
                  type="text"
                  placeholder="Longhorn Collective"
                  value={fields.org}
                  onChange={handleChange}
                  required
                  autoComplete="organization"
                />
              </div>

              <div className="demo-field">
                <label htmlFor="demo-role">Role / Title</label>
                <input
                  id="demo-role"
                  name="role"
                  type="text"
                  placeholder="Compliance Director"
                  value={fields.role}
                  onChange={handleChange}
                  autoComplete="organization-title"
                />
              </div>

              <div className="demo-field">
                <label htmlFor="demo-message">Message <span style={{ color: '#bbb', fontWeight: 400 }}>(optional)</span></label>
                <textarea
                  id="demo-message"
                  name="message"
                  placeholder="Anything you'd like us to know before the call..."
                  value={fields.message}
                  onChange={handleChange}
                />
              </div>

              <button
                type="submit"
                className="demo-submit"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                      <circle cx="7" cy="7" r="5.5" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
                      <path d="M7 1.5A5.5 5.5 0 0112.5 7" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Sending…
                  </>
                ) : 'Request a Demo'}
              </button>

              {status === 'error' && (
                <div className="demo-error-note">
                  Something went wrong. Email us at{' '}
                  <a href="mailto:info@getarch.co" style={{ color: '#b91c1c', fontWeight: 500 }}>info@getarch.co</a>
                </div>
              )}

              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default DemoForm;
